import { NotificationDto } from "../models/notifications/notification.interface";
import { WhiteLabelAppId } from "../providers/white-label-theme.provider";

/**
 * Destino da ação contextual de um tipo de notificação (DEV-895, CA 01 e CA 02).
 *
 * O backend NÃO manda rota: o payload não tem campo de link, href ou deep link — só `data` com os
 * ids já protegidos (`AssignmentAttributorNotifier.BuildData`). Logo o de:para typeKey → tela mora
 * aqui, do lado que conhece as rotas. É o ÚNICO conhecimento de domínio que este módulo carrega: a
 * frase continua vindo montada do servidor, e o front não escreve copy por tipo.
 *
 * `app` é o que permite a ação existir nos dois shells: `/manage-assignment/:idAssignment` é rota do
 * platform (`apps/platform/src/app/app.routes.ts:44`) e não existe no admin, então o destino declara
 * ONDE a tela mora e quem resolve decide entre `Router` e salto entre apps.
 *
 * Um destino só serve a TODOS os perfis, e não é descuido: os dois tipos vão para todo perfil porque
 * quem decide o envolvimento é o vínculo de atribuidor, não o papel — logo quem recebe o aviso é,
 * por construção, atribuidor daquela atribuição, e a tela de destino é a dela. Um gestor que atribui
 * Formação entra ali pelo mesmo vínculo que um professor. É o que dispensa um destino por papel.
 */
export interface NotificationTargetDefinition {
  readonly app: WhiteLabelAppId;
  /** Segmentos fixos da rota, sem o id e sem barras. Quem monta a URL cuida das barras. */
  readonly segments: readonly string[];
  /** Chave de `data` que carrega o id do destino, anexado ao fim da rota. */
  readonly dataKey: string;
  /**
   * Rótulo do botão. Mora no destino, e não num mapa separado por tipo, para que CTA e destino não
   * possam divergir: um botão que não leva a lugar nenhum é pior do que linha sem botão.
   */
  readonly ctaLabel: string;
}

export interface ResolvedNotificationTarget {
  readonly app: WhiteLabelAppId;
  /** Pronto para `Router.navigate`, no formato do precedente `action.component.ts:262`. */
  readonly commands: readonly string[];
  /** Pronto para concatenar à URL do app no salto entre apps. Sem barra inicial. */
  readonly path: string;
  readonly ctaLabel: string;
}

/**
 * A tela de atribuição. Entrar e sair levam ao MESMO lugar: são o par do mesmo fato — mesma razão
 * pela qual o backend dá um único interruptor de preferência aos dois (`movimentacao_equipe`).
 */
const VIEW_ASSIGNMENT: NotificationTargetDefinition = {
  app: "platform",
  segments: ["manage-assignment"],
  dataKey: "idAssignment",
  ctaLabel: "Ver atribuição"
};

/** Tipos com ação contextual. Tipo ausente daqui renderiza sem CTA, e isso é estado válido. */
const TARGET_BY_TYPE_KEY: Readonly<Record<string, NotificationTargetDefinition>> = {
  "assignment.attributor.joined": VIEW_ASSIGNMENT,
  "assignment.attributor.left": VIEW_ASSIGNMENT
};

/**
 * `null` quando o tipo não declara ação OU quando o payload não trouxe o id do destino. O segundo
 * caso importa: `data` é `{}` por contrato quando vazio, e um CTA que abre
 * `/manage-assignment/undefined` é pior do que linha sem CTA.
 */
export function resolveNotificationTarget(dto: NotificationDto): ResolvedNotificationTarget | null {
  const definition = TARGET_BY_TYPE_KEY[dto.typeKey];

  if (!definition) {
    return null;
  }

  const id = dto.data[definition.dataKey];

  if (!id) {
    return null;
  }

  const route = definition.segments.join("/");

  return {
    app: definition.app,
    commands: [`/${route}`, id],
    // O id é Base64Url por construção (`AesDeterministicProtector.Base64UrlEncode`), então o encode
    // é no-op hoje — está aqui para que um formato diferente de id não vire URL quebrada em silêncio.
    path: `${route}/${encodeURIComponent(id)}`,
    ctaLabel: definition.ctaLabel
  };
}
